import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Cell, Row, useFlexLayout, usePagination, useRowSelect, useSortBy, useTable } from 'react-table';

import axios from 'axios';
import { getFSEDashboard, openFSEJob } from '../../actions/manageDashboardActions';
import { constants } from '../../constants/constants';
import { IdashboardData, IdashboardItem, Ijob } from '../../models';
import { jobTypesIdEnum, PartStatusEnum } from '../../models-enums';
import { IinitialState } from '../../reducers';
import { getOfflineJobStatus, selectIsOnlineAndEmptyQueue } from '../../reducers/commonReducers';
import { getDashboardData, getDashboardIsLoading } from '../../reducers/dashboardReducer';
// import { initialOfflineJobStatus } from '../../reducers/initialState';
// import { getJobsByID } from '../../reducers/manageJobReducer';

import {
    getSavedDataParams,
    initialDataParams,
    setSavedDataParams,
    applyUserDefaultSort,
    getInitialDataParamsWithUserDefault
} from './dashboardUtils';

import { Button } from 'react-bootstrap';
import { toastr } from 'react-redux-toastr';
import debounce from 'lodash/debounce';
import { initialOfflineJobStatus } from '../../reducers/initialState';
import { getJobsByID } from '../../reducers/manageJobReducer';
import AddFacilityModal from '../manageJob/AddFacilityModal';
import EditJobModal from '../manageJob/EditJobModal';
import SearchFacilityModal from '../manageJob/SearchFacilityModal';
import DashboardSearch from './DashboardSearch';
import DashboardSidebarCreate from './DashboardSidebarCreate';
import DashboardSidebarWorkOrder from './DashboardSidebarWorkOrder';
import DashboardSummary from './DashboardSummary';
import { getColumns, getMobileColumns } from './getColumns';
import { LoadingOverlay } from './LoadingOverlay';

const todayMinus30Days = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

export interface Iprops {
    loading: boolean;
    online: boolean;
    isAPIQueueEmpty: boolean;
    isMobile: boolean;
    isTablet: boolean;
    selectedJob: Ijob;
}

const Dashboard = (props: Iprops & WithTranslation) => {
    const { t, isAPIQueueEmpty, online, isMobile, isTablet } = props;
    const dispatch = useDispatch();
    const history = useHistory();
    const currentUser = useSelector((state: IinitialState) => state.user);
    const [dataParams, setDataParams] = useState(() =>
        applyUserDefaultSort(getSavedDataParams(), currentUser?.defaultDashboardSort)
    );
    const [selectedRow, setSelectedRow] = useState<IdashboardItem | undefined>(undefined);
    const [checkedRows, setCheckedRows] = useState<IdashboardItem[]>([]);
    const [checkedPartStatusCounts, setCheckedPartStatusCounts] = useState([0, 0, 0]);
    const [modalJWOVisible, setModalJWOVisible] = useState(false);
    const [modalNEWVisible, setModalNEWVisible] = useState(false);
    const [facilityFilter, setFacilityFilter] = useState<string | null>(null);
    // Bumped when a job was created but we stayed on the dashboard. The cached list still shows that
    // work order as job-less, so it keeps offering "Start Job" on a WO that now has one - and the
    // FSE starting it again is what produces a duplicate job on the same work order.
    const [refreshToken, setRefreshToken] = useState(0);

    // Navigation state for tracking position in the flattened navigation sequence
    const [, setNavigationState] = useState<{
        currentSequenceIndex: number; // tracks position in flattened sequence
    }>({
        currentSequenceIndex: -1
    });

    const data: IdashboardData = useSelector(getDashboardData);
    const loading: boolean = useSelector(getDashboardIsLoading);

    const jobs = useSelector(getJobsByID);
    const offlineJobStatus = useSelector(getOfflineJobStatus);

    // Use refs to store the latest values to avoid infinite loops
    const jobsRef = useRef(jobs);
    const offlineJobStatusRef = useRef(offlineJobStatus);

    // Update refs when values change
    jobsRef.current = jobs;
    offlineJobStatusRef.current = offlineJobStatus;

    // Create flattened navigation sequence from dashboard items
    const createNavigationSequence = useCallback((): IdashboardItem[] => {
        const sequence: IdashboardItem[] = [];
        data.items.forEach(item => {
            sequence.push(item); // Add parent record
            if (item.children && item.children.length > 0) {
                sequence.push(...item.children); // Add child records
            }
        });
        return sequence;
    }, [data.items]);

    // Helper to determine if a record is a parent record (not a child)
    const isParentRecord = useCallback(
        (item: IdashboardItem): boolean => {
            // A record is a parent if it exists in data.items AND is not found as a child of another record
            const isInDataItems = data.items.some(parentItem => parentItem.id === item.id);
            if (!isInDataItems) return false;

            // Check if this item appears as a child of any other item
            const isChildOfAnotherItem = data.items.some(parentItem =>
                parentItem.children?.some(child => child.id === item.id)
            );

            return !isChildOfAnotherItem;
        },
        [data.items]
    );

    // Helper to find the parent of a child record
    const findParentOfChild = useCallback(
        (childItem: IdashboardItem): IdashboardItem | null => {
            for (const parentItem of data.items) {
                if (parentItem.children?.some(child => child.id === childItem.id)) {
                    return parentItem;
                }
            }
            return null;
        },
        [data.items]
    );

    // Get only parent records for parent-to-parent navigation
    const getParentRecords = useCallback((): IdashboardItem[] => {
        return data.items;
    }, [data.items]);

    // Helper to determine if a row should be highlighted (selected row is this row or one of its children)
    const shouldHighlightRow = useCallback(
        (rowData: IdashboardItem): boolean => {
            if (!selectedRow) return false;

            // Direct match
            if (rowData.id === selectedRow.id) return true;

            // Check if selectedRow is a child of this row
            return rowData.children?.some(child => child.id === selectedRow.id) || false;
        },
        [selectedRow]
    );

    // Calculate navigation position within parent/child group (1/4, 2/4, etc.)
    const getNavigationPosition = useCallback(
        (item: IdashboardItem): { current: number; total: number } | null => {
            // First check if this is a parent record
            const isParent = isParentRecord(item);

            if (isParent) {
                // For parent records, check if they have children
                if (item.children && item.children.length > 0) {
                    // Parent is position 1, total includes parent + children
                    return { current: 1, total: item.children.length + 1 };
                }
                // No children, don't show position indicator
                return null;
            } else {
                // For child records, find parent and calculate position
                const parent = findParentOfChild(item);
                if (parent && parent.children) {
                    const childIndex = parent.children.findIndex(child => child.id === item.id);
                    if (childIndex !== -1) {
                        // Child position is childIndex + 2 (because parent is 1)
                        const position = { current: childIndex + 2, total: parent.children.length + 1 };

                        return position;
                    }
                }

                return null;
            }
        },
        [isParentRecord, findParentOfChild]
    );

    const getJobIsDownloaded = useCallback(
        (jobID: string): boolean => {
            // because the jobs from the dashboard are not utilizing existing job state
            // we need to pull from it first
            const job = jobsRef.current[jobID];
            if (!job) return false;
            const jobStatus = offlineJobStatusRef.current[job.id] || initialOfflineJobStatus;
            if (job.jobTypeID === jobTypesIdEnum.verification && jobStatus.simpleMeasurementPointResults === false)
                return false;
            if (jobStatus.simpleMeasurementPointResults === false) return false;
            return true;
        },
        [] // No dependencies needed since we use refs
    );

    useEffect(() => {
        // comment out if you want to mess w/css faster

        // Only fetch dashboard when 0 or 1 rows are checked
        if (checkedRows.length > 1) return;

        const source = axios.CancelToken.source();

        // Apply facility filter if set and reset to page 0 when filtering
        const paramsWithFilter = facilityFilter
            ? { ...dataParams, search: facilityFilter, page: 0 }
            : { ...dataParams };

        dispatch(getFSEDashboard(paramsWithFilter, source.token));
        setSavedDataParams(paramsWithFilter);

        if (checkedRows.length === 0) {
            setSelectedRow(undefined);
            setCheckedRows([]);
            setFacilityFilter(null);
            setSavedDataParams(paramsWithFilter);
        }

        return () => source.cancel();
    }, [dataParams, dispatch, checkedRows.length, facilityFilter, refreshToken]);

    useEffect(() => {
        // sum up the part status for delivered (green), shipped (yellow), and ordered (red)
        let deliveredCount = 0;
        let shippedCount = 0;
        let orderedCount = 0;

        checkedRows.forEach((item: IdashboardItem) => {
            if (item.workOrder?.partStatus === PartStatusEnum.Delivered) deliveredCount++;
            if (item.workOrder?.partStatus === PartStatusEnum.Shipped) shippedCount++;
            if (item.workOrder?.partStatus === PartStatusEnum.Ordered) orderedCount++;
        });

        setCheckedPartStatusCounts([deliveredCount, shippedCount, orderedCount]);
    }, [checkedRows]);

    // modal form pop-up on selected job change
    useEffect(() => {
        // if (selectedRow?.id) console.log('selected', selectedRow?.id, selectedRow?.job?.status);
        if (/* selectedRow?.job?.id &&*/ !modalJWOVisible && !modalNEWVisible)
            setTimeout(() => setModalJWOVisible(true), 100);
        // eslint-disable-next-line
    }, [selectedRow]);

    const onClearSearch = () => {
        // retain page size and reset to page 0, using user's default sort preference
        const resetParams = getInitialDataParamsWithUserDefault(currentUser?.defaultDashboardSort);
        setDataParams({ ...resetParams, pageSize: dataParams.pageSize, page: 0 });
        // Clear react-table's selection state
        toggleAllRowsSelected(false);
    };

    const onClearSummary = () => {
        setCheckedRows([]);
        setFacilityFilter(null);
        // Reset to user's default sort preference
        const resetParams = getInitialDataParamsWithUserDefault(currentUser?.defaultDashboardSort);
        setDataParams({ ...resetParams, pageSize: dataParams.pageSize, search: '', page: 0 });
        // Clear react-table's selection state
        toggleAllRowsSelected(false);
    };

    const onPageChange = useCallback((page: number) => {
        setDataParams((prev: any) => ({ ...prev, page }));
    }, []);

    const handleSort = useCallback(
        (columnId: string) => {
            const sort = columnId;
            const descending = dataParams.sort === columnId && !dataParams.descending;
            setDataParams({
                ...dataParams,
                sort,
                descending
            });
        },
        [dataParams]
    );

    const onPageSizeChange = useCallback((newPageSize: number, newPage: number) => {
        setDataParams((prev: any) => ({ ...prev, page: newPage, pageSize: newPageSize }));
    }, []);

    // Track window width to ensure columns update on orientation changes
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const debouncedResize = debounce(() => setWindowWidth(window.innerWidth), 200);
        
        const handleOrientationChange = () => {
            // Flush any pending debounced calls and update immediately
            debouncedResize.cancel();
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', debouncedResize);
        window.addEventListener('orientationchange', handleOrientationChange);
        
        return () => {
            window.removeEventListener('resize', debouncedResize);
            window.removeEventListener('orientationchange', handleOrientationChange);
            debouncedResize.cancel();
        };
    }, []);

    // Determine if mobile layout should be used (for columns and flex layout)
    // Use mobile columns (with labels) for mobile and tablet portrait (< 1024px)
    // The CSS grid layout with labels is active from 414px-1023px
    const useMobileLayout = isMobile || (isTablet && windowWidth < 1024);

    const columns = useMemo(() => {
        if (useMobileLayout) {
            return getMobileColumns({ t, data, getJobIsDownloaded });
        }

        return getColumns({
            t,
            data,
            getJobIsDownloaded
        });
    }, [data, useMobileLayout, t, getJobIsDownloaded]);

    // Helper functions to determine button states and visibility
    const getNavigationButtonStates = () => {
        if (!selectedRow) {
            return { isPrevDisabled: true, isNextDisabled: true, isNavigationVisible: false };
        }

        const parentRecords = getParentRecords();

        // Only show navigation if there are multiple parent records or current record has children
        const hasMultipleParents = parentRecords.length > 1;
        const hasChildren = selectedRow.children && selectedRow.children.length > 0;
        const isInChildSequence = !isParentRecord(selectedRow);

        if (!hasMultipleParents && !hasChildren && !isInChildSequence) {
            return { isPrevDisabled: true, isNextDisabled: true, isNavigationVisible: false };
        }

        let isPrevDisabled = false;
        let isNextDisabled = false;

        if (isParentRecord(selectedRow)) {
            // For parent records
            const currentParentIndex = parentRecords.findIndex(item => item.id === selectedRow.id);

            // Previous is disabled if this is the first parent record
            isPrevDisabled = currentParentIndex === 0;

            // Next is disabled if this is the last parent and has no children
            isNextDisabled =
                currentParentIndex === parentRecords.length - 1 &&
                (!selectedRow.children || selectedRow.children.length === 0);
        } else {
            // For child records
            const parent = findParentOfChild(selectedRow);
            if (parent && parent.children) {
                const childIndex = parent.children.findIndex(child => child.id === selectedRow.id);
                const parentIndex = parentRecords.findIndex(item => item.id === parent.id);

                // For child records, we can always go back:
                // - If not the first child: go back to previous child
                // - If the first child: go back to parent
                isPrevDisabled = false;

                // Next is disabled if this is the last child of the last parent
                isNextDisabled = childIndex === parent.children.length - 1 && parentIndex === parentRecords.length - 1;
            }
        }

        return { isPrevDisabled, isNextDisabled, isNavigationVisible: true };
    };

    const { isPrevDisabled, isNextDisabled, isNavigationVisible } = getNavigationButtonStates();

    const handlePrevWorkOrder = () => {
        if (!selectedRow) return;

        if (isParentRecord(selectedRow)) {
            // From parent record: go to previous parent record (skip children)
            const parentRecords = getParentRecords();
            const currentParentIndex = parentRecords.findIndex(item => item.id === selectedRow.id);

            if (currentParentIndex > 0) {
                const prevParent = parentRecords[currentParentIndex - 1];
                if (prevParent) {
                    setSelectedRow(prevParent);

                    // Update navigation state
                    const navigationSequence = createNavigationSequence();
                    const sequenceIndex = navigationSequence.findIndex(item => item.id === prevParent.id);
                    setNavigationState({
                        currentSequenceIndex: sequenceIndex
                    });
                }
            }
        } else {
            // From child record: navigate within child sequence or back to parent
            const parent = findParentOfChild(selectedRow);
            if (parent && parent.children) {
                const childIndex = parent.children.findIndex(child => child.id === selectedRow.id);

                if (childIndex > 0) {
                    // Go to previous child
                    const prevChild = parent.children[childIndex - 1];
                    if (prevChild) {
                        setSelectedRow(prevChild);

                        const navigationSequence = createNavigationSequence();
                        const sequenceIndex = navigationSequence.findIndex(item => item.id === prevChild.id);
                        setNavigationState({
                            currentSequenceIndex: sequenceIndex
                        });
                    }
                } else {
                    // Go back to parent (first child going back to parent)
                    setSelectedRow(parent);

                    const navigationSequence = createNavigationSequence();
                    const sequenceIndex = navigationSequence.findIndex(item => item.id === parent.id);
                    setNavigationState({
                        currentSequenceIndex: sequenceIndex
                    });
                }
            }
        }
    };

    const handleNextWorkOrder = () => {
        if (!selectedRow) return;

        if (isParentRecord(selectedRow)) {
            // From parent record: go to first child if exists, otherwise next parent
            if (selectedRow.children && selectedRow.children.length > 0) {
                // Go to first child
                const firstChild = selectedRow.children[0];
                if (firstChild) {
                    setSelectedRow(firstChild);

                    const navigationSequence = createNavigationSequence();
                    const sequenceIndex = navigationSequence.findIndex(item => item.id === firstChild.id);
                    setNavigationState({
                        currentSequenceIndex: sequenceIndex
                    });
                }
            } else {
                // No children, go to next parent record
                const parentRecords = getParentRecords();
                const currentParentIndex = parentRecords.findIndex(item => item.id === selectedRow.id);

                if (currentParentIndex < parentRecords.length - 1) {
                    const nextParent = parentRecords[currentParentIndex + 1];
                    if (nextParent) {
                        setSelectedRow(nextParent);

                        const navigationSequence = createNavigationSequence();
                        const sequenceIndex = navigationSequence.findIndex(item => item.id === nextParent.id);
                        setNavigationState({
                            currentSequenceIndex: sequenceIndex
                        });
                    }
                }
            }
        } else {
            // From child record: navigate within child sequence or to next parent
            const parent = findParentOfChild(selectedRow);
            if (parent && parent.children) {
                const childIndex = parent.children.findIndex(child => child.id === selectedRow.id);

                if (childIndex < parent.children.length - 1) {
                    // Go to next child
                    const nextChild = parent.children[childIndex + 1];
                    if (nextChild) {
                        setSelectedRow(nextChild);

                        const navigationSequence = createNavigationSequence();
                        const sequenceIndex = navigationSequence.findIndex(item => item.id === nextChild.id);
                        setNavigationState({
                            currentSequenceIndex: sequenceIndex
                        });
                    }
                } else {
                    // Last child, go to next parent record
                    const parentRecords = getParentRecords();
                    const currentParentIndex = parentRecords.findIndex(item => item.id === parent.id);

                    if (currentParentIndex < parentRecords.length - 1) {
                        const nextParent = parentRecords[currentParentIndex + 1];
                        if (nextParent) {
                            setSelectedRow(nextParent);

                            const navigationSequence = createNavigationSequence();
                            const sequenceIndex = navigationSequence.findIndex(item => item.id === nextParent.id);
                            setNavigationState({
                                currentSequenceIndex: sequenceIndex
                            });
                        }
                    }
                }
            }
        }
    };

    const handleOpenOrDownload = () => {
        setModalJWOVisible(false);
        const id = selectedRow?.job?.id || '';
        if (id) dispatch(openFSEJob(id, history));
    };

    const handleOpenOrDownloadWithId = async (id: string) => {
        if (!id) {
            throw new Error('Missing job id to handle open and download');
        }

        setModalNEWVisible(false);

        // The job already exists on the server by the time we get here. If opening it fails we stay
        // on the dashboard, so refresh the list: the row must flip from "Start Job" to "Open" rather
        // than inviting the FSE to start the same work order a second time.
        // useDispatch() here is not thunk-aware, so the thunk's resolved value needs an explicit cast.
        const opened = await (dispatch(openFSEJob(id, history)) as unknown as Promise<boolean>);
        if (!opened) {
            setCheckedRows([]);
            toggleAllRowsSelected(false);
            setRefreshToken(token => token + 1);
        }
    };

    const handleStartJob = () => {
        setModalJWOVisible(false);
        setModalNEWVisible(true);
    };

    const onStartJob = () => {
        const indicators = checkedRows.map(x => x.workOrder?.sapBusinessIndicator);
        // We just want to ensure that all the SAP Business Indicators are the same.
        // We can do this by checking if any of the values are different.
        // If they are we disable the modal from opening
        const isDisabled = indicators.some((value, index, array) => value !== array[0]);

        if (isDisabled) {
            toastr.error(
                'Please select work orders with the same SAP Business Indicator',
                'Error',
                constants.toastrError
            );
        } else {
            setModalNEWVisible(true);
        }
    };

    const tableInstance = useTable(
        {
            columns,
            data: data.items,
            initialState: {
                pageIndex: Math.max(0, dataParams.page),
                pageSize: dataParams.pageSize,
                sortBy: dataParams.sort ? [{ id: dataParams.sort, desc: dataParams.descending }] : []
            },
            manualPagination: true,
            manualSortBy: true,
            pageCount: Math.ceil(data.totalCount / dataParams.pageSize),
            // Custom selection handlers
            getRowId: (row: IdashboardItem) => row.id,
            autoResetSelectedRows: false
        } as any,
        useSortBy,
        usePagination,
        useRowSelect,
        // Only use flex layout on desktop (1024px+), not on mobile/tablet
        ...(!useMobileLayout ? [useFlexLayout] : [])
    ) as any;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        pageCount,
        pageSize,
        setPageSize,
        canPreviousPage,
        canNextPage,
        gotoPage,
        nextPage,
        previousPage,
        toggleAllRowsSelected,
        state: { pageIndex, selectedRowIds }
    } = tableInstance;

    const onDashboardSearchValueChanges = (values: { [key: string]: any }) => {
        setDataParams({
            ...dataParams,
            ...values
        });
        gotoPage(0);
    };

    // Sync react-table state with component state
    useEffect(() => {
        if (pageIndex !== dataParams.page && pageIndex >= 0) {
            onPageChange(pageIndex);
        }
    }, [pageIndex, dataParams.page, onPageChange]);

    useEffect(() => {
        if (pageSize !== dataParams.pageSize && pageSize > 0) {
            onPageSizeChange(pageSize, 0);
        }
    }, [pageSize, dataParams.pageSize, onPageSizeChange]);

    // Handle row selection changes for facility filtering
    useEffect(() => {
        const selectedIds = Object.keys(selectedRowIds);
        const selectedItems = selectedIds
            .map(id => data.items.find(item => item.id === id))
            .filter(Boolean) as IdashboardItem[];

        if (selectedItems.length === 0) {
            // No selections - clear checked rows and facility filter if it was set
            const hadFacilityFilter = facilityFilter !== null;
            setCheckedRows([]);
            setFacilityFilter(null);

            // Only reset to page 0 if we had a facility filter before (meaning we're clearing a filter)
            if (hadFacilityFilter && dataParams.page !== 0) {
                setDataParams((prev: any) => ({ ...prev, page: 0 }));
                gotoPage(0);
            }
        } else if (selectedItems.length === 1 && selectedItems[0]) {
            // First selection - set facility filter
            const firstItem = selectedItems[0];
            const facilityName = firstItem?.facility?.name || '';

            setFacilityFilter(facilityName);
            setCheckedRows(selectedItems);

            // Always reset to page 0 when applying a new facility filter
            if (dataParams.page !== 0) {
                setDataParams((prev: any) => ({ ...prev, page: 0 }));
                gotoPage(0);
            }
        } else {
            // Multiple selections - just update checked rows
            setCheckedRows(selectedItems);
        }
    }, [selectedRowIds, data.items, dataParams.page, gotoPage, facilityFilter]);

    const atLeastOneRowChecked = checkedRows.length > 0;

    return (
        <div className={`dashboard ${atLeastOneRowChecked ? 'dashboard-with-summary' : 'dashboard-with-search'}`}>
            {!atLeastOneRowChecked ? (
                <DashboardSearch
                    loading={loading}
                    initialParams={initialDataParams}
                    params={dataParams}
                    partStatusCounts={[data.totalPartStatus3, data.totalPartStatus2, data.totalPartStatus1]}
                    onValueChanges={onDashboardSearchValueChanges}
                    onClear={onClearSearch}
                    online={online}
                    isAPIQueueEmpty={isAPIQueueEmpty}
                />
            ) : (
                <DashboardSummary
                    isMobile={isMobile || isTablet}
                    loading={loading}
                    items={checkedRows}
                    partStatusCounts={checkedPartStatusCounts}
                    onStartJob={onStartJob}
                    onClear={onClearSummary}
                />
            )}

            <LoadingOverlay loading={loading} loadingText={'Loading...'} />
            <div {...getTableProps()} className={`ReactTable beacon-table -highlight blue ${!useMobileLayout ? 'flex-layout' : ''}`}>
                <div className="rt-thead -header sticky-header">
                    {headerGroups.map((headerGroup: any) => (
                        <div
                            {...headerGroup.getHeaderGroupProps()}
                            key={headerGroup.getHeaderGroupProps().key}
                            className="rt-tr"
                        >
                            {headerGroup.headers.map((column: any) => (
                                <div
                                    {...column.getHeaderProps({
                                        className: `rt-th ${
                                            column.id !== 'selection' && dataParams.sort === column.id
                                                ? dataParams.descending
                                                    ? '-sort-desc'
                                                    : '-sort-asc'
                                                : ''
                                        }`
                                    })}
                                    key={column.getHeaderProps().key}
                                    onClick={() => {
                                        if (column.id !== 'selection') {
                                            handleSort(column.id);
                                        }
                                    }}
                                >
                                    <div>{column.render('Header')}</div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <div {...getTableBodyProps()} className="rt-tbody">
                    {page.map((row: Row<IdashboardItem>, i: number) => {
                        prepareRow(row);
                        const rowData = row.original as IdashboardItem;
                        const isEvenRow = i % 2 === 0;
                        return (
                            <div
                                {...row.getRowProps()}
                                key={row.getRowProps().key}
                                className={`rt-tr ${isEvenRow ? '-odd' : '-even'}`}
                                style={{
                                    background: shouldHighlightRow(rowData) ? constants.colors['blueTr'] : '',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    const newSelectedRow = selectedRow?.id === rowData.id ? undefined : rowData;
                                    setSelectedRow(newSelectedRow);
                                    // Initialize navigation state when selecting a new row from the main dashboard
                                    if (newSelectedRow && newSelectedRow.id !== selectedRow?.id) {
                                        const navigationSequence = createNavigationSequence();
                                        const sequenceIndex = navigationSequence.findIndex(
                                            item => item.id === newSelectedRow.id
                                        );

                                        setNavigationState({
                                            currentSequenceIndex: sequenceIndex
                                        });
                                    }
                                }}
                            >
                                {row.cells.map((cell: Cell<IdashboardItem>) => {
                                    // Check if this is the workOrderNumber column and if it's overdue
                                    const isWorkOrderCell = cell.column.id === 'workOrderNumber';
                                    const isOverdue =
                                        isWorkOrderCell && rowData.workOrder?.dueDate
                                            ? todayMinus30Days > new Date(rowData.workOrder.dueDate)
                                            : false;

                                    const columnClassName = (cell.column as any).className;

                                    return (
                                        <div
                                            {...cell.getCellProps()}
                                            key={cell.getCellProps().key}
                                            className={`rt-td ${columnClassName || ''} ${isOverdue ? 'warn' : ''}`}
                                        >
                                            {cell.render('Cell')}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="table-pagination">
                <Button
                    onClick={() => {
                        previousPage();
                    }}
                    variant="outline"
                    className="prev"
                    disabled={!canPreviousPage || loading}
                >
                    {t('common:previous')}
                </Button>

                <div className="controls">
                    <label htmlFor="page-input">{t('common:pageText')} </label>
                    <span>
                        <input
                            id="page-input"
                            type="number"
                            min={1}
                            max={pageCount}
                            className="page-input"
                            defaultValue={pageIndex + 1}
                            key={`page-input-${pageIndex}`}
                            disabled={loading}
                            onChange={e => {
                                const gotoPageNum = e.target.value ? Number(e.target.value) - 1 : 0;
                                if (gotoPageNum >= 0 && gotoPageNum < pageCount) {
                                    gotoPage(gotoPageNum);
                                }
                            }}
                            onWheel={e => e.currentTarget.blur()}
                        />
                    </span>
                    <span>{t('common:of')} </span>
                    <span>{pageCount}</span>

                    <select
                        name="page-size-select"
                        value={dataParams.pageSize}
                        disabled={loading}
                        className="table-page-size-select"
                        onChange={e => {
                            setPageSize(Number(e.target.value));
                            setDataParams({ ...dataParams, page: 0, pageSize: Number(e.target.value) });
                        }}
                    >
                        {constants.tablePageSizeOptions.map(size => (
                            <option key={size} value={size}>
                                {size} {t('common:rows')}
                            </option>
                        ))}
                    </select>
                </div>

                <Button
                    onClick={() => {
                        nextPage();
                    }}
                    variant="outline"
                    className="next"
                    disabled={!canNextPage || loading}
                >
                    {t('common:next')}
                </Button>
            </div>

            <DashboardSidebarWorkOrder
                visible={modalJWOVisible}
                item={selectedRow}
                onUnderlayClick={() => {
                    setSelectedRow(undefined);
                    setModalJWOVisible(false);
                }}
                onCancel={() => {
                    setSelectedRow(undefined);
                    setModalJWOVisible(false);
                }}
                onPrev={handlePrevWorkOrder}
                onNext={handleNextWorkOrder}
                onOpen={handleOpenOrDownload}
                handleStartJob={handleStartJob}
                isPrevDisabled={isPrevDisabled}
                isNextDisabled={isNextDisabled}
                isNavigationVisible={isNavigationVisible}
                navigationPosition={selectedRow ? getNavigationPosition(selectedRow) : null}
            />

            <DashboardSidebarCreate
                visible={modalNEWVisible}
                selectedRow={selectedRow}
                selectedItems={checkedRows}
                onUnderlayClick={() => setModalNEWVisible(false)}
                onCancel={() => setModalNEWVisible(false)}
                onStart={handleOpenOrDownloadWithId}
            />

            <SearchFacilityModal colorButton={constants.colors['red']} secondModal={false} />

            <AddFacilityModal colorButton={constants.colors['blue']} secondModal={true} />

            <EditJobModal colorButton={constants.colors['blue'] || ''} secondModal={true} dispatch={dispatch} />
        </div>
    );
};

const mapStateToProps = (state: IinitialState, ownProps: Iprops) => {
    return {
        online: state.offline.online,
        isAPIQueueEmpty: selectIsOnlineAndEmptyQueue(state),
        isMobile: state.config.isMobile,
        isTablet: state.config.isTablet,
        jobNumber: state.manageJob.selectedJob.jobNumber
    };
};

export default withTranslation('manageWorkOrder')(connect(mapStateToProps, {})(Dashboard));
